/* eslint-disable max-statements */
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as SalesDocumentsActions from './sales-documents.actions';
import {
    ClockService,
    Delivery,
    merge,
    Operation,
    operationIndexFunc,
    putOperationInOrder,
    removeUnavailableDocumentsFromState,
    SalesDocument,
    DocumentAttachment,
    SalesDocumentConnection,
    SalesDocumentPosition,
    State,
    Synced,
    Timestamp,
    IccDocument,
    IccDocumentPosition,
    DocumentType,
    ComplaintAppointment,
    ComplaintSparePart,
    ComplaintNote,
} from '@icc/b2b/shared';
import { captureMessage } from '@sentry/angular-ivy';
import { flagUnavailableDocuments } from '../synchronization/remove';

export const SALES_DOCUMENTS_FEATURE_KEY = 'salesDocuments';
export const COMPLAINTS_DOCUMENTS_FEATURE_KEY = 'complaints';

export interface SalesDocumentsPartialState {
    readonly [SALES_DOCUMENTS_FEATURE_KEY]: State;
}

export const salesDocumentsAdapter: EntityAdapter<SalesDocument> =
    createEntityAdapter<SalesDocument>();

export const initialState: State = {
    // set initial required properties
    loaded: false,
    documents: {},
    documentsIds: [],
    documentTypes: new Map<string, DocumentType>(),
    positions: {},
    appointments: {},
    complaintNotes: {},
    complaintSpareParts: {},
    attachments: [],
    deliveries: {},
    operations: [],
    operationsKeysMap: new Map<string, Operation>(),
    operationsIds: new Map<string, true>(),
    operationsIdByDocs: new Map<string, Map<string, true>>(),
    opsToSendToWorker: [],
    opsToSend: [],
    opsToSave: [],
    staleDocs: new Map<string, number>(),
    documentConnections: {},
    userId: 0,
    userCompanyId: 0,
    userMarketId: 0,
    userEmail: '',
    userPhoneNumber: '',
    userReferenceName: '',
    synchronizationInProgress: false,
    changesOnServer: 0,
    synced: new Map<string, Synced>(),

    graphsToSend: new Map<string, number>(),
    savedTags: new Map<string, boolean>(),
};

const salesDocumentsReducer = createReducer(
    initialState,
    on(SalesDocumentsActions.init, (state) => ({ ...state, loaded: false, error: null })),
    on(
        SalesDocumentsActions.mergeOps,
        (state, { operations, statusSystems, selectedId, syncKey }) => ({
            ...merge(
                operations,
                state,
                ClockService.getClock(),
                statusSystems,
                ClockService.deletedDocs,
                syncKey == null
            ),
            selectedId: selectedId ?? state.selectedId,
        })
    ),
    on(
        SalesDocumentsActions.loadSalesDocumentsSuccess,
        (
            state,
            {
                documents,
                documentsIds,
                ops,
                operationsKeysMap,
                operationsIds,
                positions,
                appointments,
                complaintNotes,
                complaintSpareParts,
                selectedId,
                userId,
                userCompanyId,
                userMarketId,
                userEmail,
                userPhoneNumber,
                userReferenceName,
                machineId,
                documentConnections,
                attachments,
                deliveries,
            }
        ) => ({
            ...state,
            documents,
            documentsIds,
            positions,
            appointments,
            complaintNotes,
            complaintSpareParts,
            attachments,
            deliveries,
            operations: ops,
            operationsKeysMap,
            operationsIds,
            documentConnections,
            loaded: true,
            selectedId,
            userId,
            userCompanyId,
            userMarketId,
            userEmail,
            userPhoneNumber,
            userReferenceName,
            machineId,
        })
    ),
    on(SalesDocumentsActions.loadSalesDocumentsFailure, (state, { error }) => ({
        ...state,
        error,
    })),
    on(SalesDocumentsActions.openDoc, (state, { id }) => ({ ...state, selectedId: id })),
    on(SalesDocumentsActions.opsSyncedOnWorker, (state, { ops }) => {
        const sentOpsMap = new Map(ops.map((op) => [op.id, op]));
        const leftOps = [...state.opsToSend.filter((op) => !sentOpsMap.has(op.id))];
        return {
            ...state,
            opsToSend: leftOps,
            changedOnServer: 0,
        };
    }),
    on(SalesDocumentsActions.updatedDocumentsOnWorker, (state, { updates }) => {
        const updatedState: State = updateDocumentsStateFromWorker(state, updates);
        Object.assign(ClockService.getClock().merklesMap, updates.graphs);
        for (const [docId, version] of Object.entries(updates.updatedGraphsSent)) {
            if (ClockService.updatedGraphs[docId] === version) {
                delete ClockService.updatedGraphs[docId];
            }
        }
        for (const [docId, version] of Object.entries(updates.updatedGraphs)) {
            if (ClockService.updatedGraphs[docId] < version) {
                ClockService.updatedGraphs[docId] = version;
            }
        }
        // console.log('RECEIVE FROM WORKER', Object.keys(updates.updatedGraphs).join(','), Object.keys(updates.graphs).map(docId => `${docId}: ${updates.graphs[docId].hash}`).join(', '));
        // console.log('AFTER RECEIVE FROM WORKER', Object.keys(ClockService.updatedGraphs).join(','), Object.keys(ClockService.getClock().merklesMap).map(docId => `${docId}: ${ClockService.getClock().merklesMap[docId].hash}`).join(', '));
        removeUnavailableDocumentsFromState(updatedState, updates.deletedDocuments);
        return updatedState;
    }),
    on(SalesDocumentsActions.deletedDocumentsOnWorker, (state, { updates }) => {
        const updatedState: State = {
            ...state,
            documents: {
                ...state.documents,
            },
            documentsIds: [...state.documentsIds],
            positions: {
                ...state.positions,
            },
            appointments: {
                ...state.appointments,
            },
            complaintNotes: {
                ...state.complaintNotes,
            },
            complaintSpareParts: {
                ...state.complaintSpareParts,
            },
            attachments: [...state.attachments],
            documentConnections: {
                ...state.documentConnections,
            },
            deliveries: {
                ...state.deliveries,
            },
            operationsIds: new Map(state.operationsIds),
            operationsKeysMap: new Map(state.operationsKeysMap),
        };
        flagUnavailableDocuments(state.operations, updatedState, updates.deletedDocuments, false);
        removeUnavailableDocumentsFromState(updatedState, updates.deletedDocuments);
        return updatedState;
    }),
    on(SalesDocumentsActions.loadedDocumentsFromDB, (state, { updates }) =>
        updateDocumentsState(state, updates)
    ),
    on(SalesDocumentsActions.updatedSyncedStateOnWorker, (state, { synced, staleDocs }) => {
        const updatedState: State = {
            ...state,
            synced: new Map(state.synced),
            staleDocs: new Map(state.staleDocs),
        };
        // console.log('updatedSyncedStateOnWorker', id, structuredClone(staleDocs), structuredClone(updatedState.staleDocs), structuredClone(synced));
        for (const [docId, sync] of synced) {
            if (!staleDocs ||
                !updatedState.staleDocs.get(docId) ||
                (staleDocs && staleDocs.get(docId) === updatedState.staleDocs.get(docId))
            ) {
                updatedState.synced.set(docId, sync);

                if (staleDocs && staleDocs.get(docId) === updatedState.staleDocs.get(docId)) {
                    updatedState.staleDocs.delete(docId);
                }
            }
        }

        return updatedState;
    }),
    on(SalesDocumentsActions.tagsSavedInDb, (state, { savedTags }) => {
        const updatedState: State = {
            ...state,
            savedTags: new Map(state.savedTags),
        };
        for (const tag of savedTags) {
            if (tag?.uuid) {
                updatedState.savedTags.set(tag.uuid, true);
            }
        }
        return updatedState;
    }),
);

function updateDocumentsState(
    state: State,
    updates: {
        documents: Record<string, IccDocument>;
        documentsIds: string[];
        documentTypes: Map<string, DocumentType>;
        positions: Record<string, IccDocumentPosition>;
        appointments: Record<string, ComplaintAppointment>;
        complaintNotes: Record<string, ComplaintNote>;
        complaintSpareParts: Record<string, ComplaintSparePart>;
        attachments: DocumentAttachment[];
        documentConnections: SalesDocumentConnection[];
        deliveries: Record<string, Delivery>;
        ops: Operation[];
    }
) {
    const updatedState: State = {
        ...state,
        documents: {
            ...state.documents,
        },
        documentsIds: [...state.documentsIds],
        documentTypes: new Map(state.documentTypes),
        positions: {
            ...state.positions,
        },
        appointments: {
            ...state.appointments,
        },
        complaintNotes: {
            ...state.complaintNotes,
        },
        complaintSpareParts: {
            ...state.complaintSpareParts,
        },
        attachments: [...state.attachments],
        documentConnections: {
            ...state.documentConnections,
        },
        deliveries: {
            ...state.deliveries,
        },
    };
    for (const docId of updates.documentsIds) {
        if (!updatedState.documentsIds.includes(docId)) {
            updatedState.documentsIds.push(docId);
            updatedState.documents[docId] = updates.documents[docId];
        }
    }

    for (const docType of updates.documentTypes) {
        updatedState.documentTypes.set(docType[0], docType[1]);
    }

    for (const positionId of Object.keys(updates.positions)) {
        if (!(updates.positions[positionId].documentId in state.documents)) {
            updatedState.positions[positionId] = updates.positions[positionId];
        }
    }

    for (const appointmentId of Object.keys(updates.appointments)) {
        if (!(updates.appointments[appointmentId].documentId in state.documents)) {
            updatedState.appointments[appointmentId] = updates.appointments[appointmentId];
        }
    }

    for (const complaintNoteId of Object.keys(updates.complaintNotes)) {
        if (!(updates.complaintNotes[complaintNoteId].documentId in state.documents)) {
            updatedState.complaintNotes[complaintNoteId] = updates.complaintNotes[complaintNoteId];
        }
    }

    for (const complaintSparePartId of Object.keys(updates.complaintSpareParts)) {
        if (!(updates.complaintSpareParts[complaintSparePartId].documentId in state.documents)) {
            updatedState.complaintSpareParts[complaintSparePartId] = updates.complaintSpareParts[complaintSparePartId];
        }
    }

    for (const attachment of updates.attachments) {
        if (
            !(attachment.documentId in state.documents) &&
            !state.attachments.find((att) => att?.uuid === attachment?.uuid)
        ) {
            updatedState.attachments.push(attachment);
        }
    }
    for (const con of updates.documentConnections) {
        if (!(con.salesDocumentId in state.documents) && !(con.buyDocumentId in state.documents)) {
            if (!updatedState.documentConnections[con.salesDocumentId]) {
                updatedState.documentConnections[con.salesDocumentId] = {};
            }
            if (
                !updatedState.documentConnections[con.salesDocumentId][
                    con.buyDocumentId + (con.isDeleted ? '^' : '')
                ]
            ) {
                updatedState.documentConnections[con.salesDocumentId][
                    con.buyDocumentId + (con.isDeleted ? '^' : '')
                ] = {};
            }
            updatedState.documentConnections[con.salesDocumentId][
                con.buyDocumentId + (con.isDeleted ? '^' : '')
            ][con.uuid] = con;
            if (
                con.isDeleted &&
                updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId]
            ) {
                delete updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId][
                    con.uuid
                ];
                if (
                    Object.keys(
                        updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId]
                    ).length === 0
                ) {
                    delete updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId];
                }
            }
        }
    }

    for (const positionId of Object.keys(updates.deliveries)) {
        if (!(updates.deliveries[positionId].documentId in state.documents)) {
            updatedState.deliveries[positionId] = updates.deliveries[positionId];
        }
    }

    for (const op of updates.ops) {
        if (!updatedState.operationsIds.has(op.id)) {
            putOperationInOrder(op, updatedState.operations);
            const key = op.index || operationIndexFunc(op);
            if (
                !updatedState.operationsKeysMap.get(key)?.timestamp ||
                updatedState.operationsKeysMap.get(key)?.timestamp < op.timestamp
            ) {
                updatedState.operationsKeysMap.set(key, op);
            }
            updatedState.operationsIds.set(op.id, true);
        }
    }
    return updatedState;
}

function updateDocumentsStateFromWorker(
    state: State,
    updates: {
        documents: Record<string, IccDocument>;
        documentsIds: string[];
        positions: Record<string, IccDocumentPosition>;
        appointments: Record<string, ComplaintAppointment>;
        complaintNotes: Record<string, ComplaintNote>;
        complaintSpareParts: Record<string, ComplaintSparePart>;
        attachments: DocumentAttachment[];
        documentConnections: SalesDocumentConnection[];
        deliveries: Record<string, Delivery>;
        ops: Operation[];
    }
) {
    const updatedState: State = {
        ...state,
        documents: {
            ...state.documents,
        },
        documentsIds: [...state.documentsIds],
        positions: {
            ...state.positions,
        },
        appointments: {
            ...state.appointments,
        },
        complaintNotes: {
            ...state.complaintNotes,
        },
        complaintSpareParts: {
            ...state.complaintSpareParts,
        },
        attachments: [...state.attachments],
        documentConnections: {
            ...state.documentConnections,
        },
        deliveries: {
            ...state.deliveries,
        },
    };

    for (const docId of updates.documentsIds) {
        if (state.documents[docId]) {
            updatedState.documents[docId] = updates.documents[docId];
        }
    }

    for (const positionId of Object.keys(updates.positions)) {
        if (updates.positions[positionId].documentId in state.documents) {
            updatedState.positions[positionId] = updates.positions[positionId];
        }
    }

    for (const appointmentId of Object.keys(updates.appointments)) {
        if (updates.appointments[appointmentId].documentId in state.documents) {
            const existing = state.appointments[appointmentId];
            const incoming = updates.appointments[appointmentId];
            if (!existing || !existing.modified || new Date(incoming.modified) >= new Date(existing.modified)) {
                updatedState.appointments[appointmentId] = incoming;
            }
        }
    }

    for (const complaintNoteId of Object.keys(updates.complaintNotes)) {
        if (updates.complaintNotes[complaintNoteId].documentId in state.documents) {
            updatedState.complaintNotes[complaintNoteId] = updates.complaintNotes[complaintNoteId];
        }
    }

    for (const complaintSparePartId of Object.keys(updates.complaintSpareParts)) {
        if (updates.complaintSpareParts[complaintSparePartId].documentId in state.documents) {
            updatedState.complaintSpareParts[complaintSparePartId] = updates.complaintSpareParts[complaintSparePartId];
        }
    }

    for (const attachment of updates.attachments) {
        if (attachment.documentId in state.documents) {
            if (!updatedState.attachments.some((el) => el.uuid === attachment.uuid)) {
                updatedState.attachments.push(attachment);
            } else {
                captureMessage('Problem z duplikowaniem się załączników', {
                    level: 'warning',
                    extra: {
                        attachmentId: attachment.uuid,
                        documentId: attachment.documentId,
                    }
                })
            }
        }
    }
    for (const con of updates.documentConnections) {
        if (con.salesDocumentId in state.documents || con.buyDocumentId in state.documents) {
            if (!updatedState.documentConnections[con.salesDocumentId]) {
                updatedState.documentConnections[con.salesDocumentId] = {};
            }
            if (
                !updatedState.documentConnections[con.salesDocumentId][
                    con.buyDocumentId + (con.isDeleted ? '^' : '')
                ]
            ) {
                updatedState.documentConnections[con.salesDocumentId][
                    con.buyDocumentId + (con.isDeleted ? '^' : '')
                ] = {};
            }
            updatedState.documentConnections[con.salesDocumentId][
                con.buyDocumentId + (con.isDeleted ? '^' : '')
            ][con.uuid] = con;
            if (
                con.isDeleted &&
                updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId]
            ) {
                delete updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId][
                    con.uuid
                ];
                if (
                    Object.keys(
                        updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId]
                    ).length === 0
                ) {
                    delete updatedState.documentConnections[con.salesDocumentId][con.buyDocumentId];
                }
            }
        }
    }

    for (const positionId of Object.keys(updates.deliveries)) {
        if (updates.deliveries[positionId].documentId in state.documents) {
            updatedState.deliveries[positionId] = updates.deliveries[positionId];
        }
    }

    for (const op of updates.ops) {
        if (
            !updatedState.operationsIds.has(op.id) &&
            (state.documents[op.docId] || ('buyDocId' in op && state.documents[op.buyDocId]))
        ) {
            putOperationInOrder(op, updatedState.operations);
            const key = op.index || operationIndexFunc(op);
            if (
                !updatedState.operationsKeysMap.get(key)?.timestamp ||
                updatedState.operationsKeysMap.get(key)?.timestamp < op.timestamp
            ) {
                updatedState.operationsKeysMap.set(key, op);
            }
            updatedState.operationsIds.set(op.id, true);
        }
    }
    return updatedState;
}

export function reducer(state: State | undefined, action: Action) {
    return salesDocumentsReducer(state, action);
}
